import React, { useEffect, useMemo, useState } from 'react';
import { makeViewTracker } from '@/lib/viewCount';
import { HlsVideo } from '@/components/common/HlsVideo';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import SectionGlow from '@/components/common/SectionGlow';
import YouTubeEmbed from '@/components/common/YouTubeEmbed';
import { AiLabelLine } from '@/components/common/AiLabel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Section 4 (01-home.md): inline Featured Video player, admin-selected.
// YouTube -> privacy-enhanced nocookie embed; R2 -> HTML5 <video> (public URL).
// Playable by everyone. Hidden entirely when no video is selected (spec Open Q3).
export const FeaturedVideo = () => {
  const { t } = useTranslation();
  const [video, setVideo] = useState(undefined); // undefined=loading, null=none

  useEffect(() => {
    axios.get(`${API}/home/featured-video`).then((r) => setVideo(r.data)).catch(() => setVideo(null));
  }, []);
  const viewTick = useMemo(() => makeViewTracker(video?.id), [video?.id]);

  const isYoutube = video?.source === 'youtube' && video?.youtube_video_id;
  const isDirect = video && video.source !== 'youtube' && video?.video_url;
  if (!video || (!isYoutube && !isDirect)) return null;

  return (
    <section data-testid="featured-video-section" className="relative overflow-hidden py-20 md:py-28" style={{ background: 'var(--bg-primary)' }}>
      <SectionGlow position="20% 20%" strength={9} />
      <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div
            data-testid="featured-video-player"
            className="rounded-2xl overflow-hidden shadow-lg"
            style={{ aspectRatio: '16 / 9', background: '#000', border: '1px solid var(--border-subtle)' }}
          >
            {isYoutube ? (
              <YouTubeEmbed
                videoId={video.youtube_video_id}
                title={video.title || t('home.featured_video.title_fallback')}
                testid="featured-video-iframe"
              />
            ) : (
              <HlsVideo
                hlsSrc={video.hls_master}
                src={video.video_url}
                hlsGated={video.hls_gated}
                videoId={video.id}
                poster={video.thumbnail || undefined}
                controls
                playsInline
                controlsList="nodownload noremoteplayback"
                disablePictureInPicture
                onContextMenu={(e) => e.preventDefault()}
                onTimeUpdate={(e) => viewTick(e.currentTarget.currentTime, e.currentTarget.duration)}
                className="w-full h-full"
                style={{ objectFit: 'contain', background: '#000' }}
              />
            )}
          </div>

          <h3 className="mt-5 text-h3 font-bold" style={{ color: 'var(--text-primary)' }} data-testid="featured-video-title">
            {video.title}
          </h3>
          <AiLabelLine label={video.ai_label} className="mt-2" testid="featured-video-ai-label" />
          {video.description && (
            <p className="mt-2 text-base" style={{ color: 'var(--text-secondary)' }} data-testid="featured-video-desc">
              {video.description}
            </p>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturedVideo;
